// src/pages/team/TeamScheduleDetailPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import {
  scheduleStatusMap,
  rotationPatternMap,
  getStatusMapping,
  getTeamName,
  formatDateTime,
  refEntity,
} from '@/features/team/logic';

export const TeamScheduleDetailPage = observer(() => {
  const { id, scheduleId } = useParams<{ id: string; scheduleId: string }>();
  const navigate = useNavigate();
  const store = useTeam();
  const { t, locale } = useI18n();

  const [schedule, setSchedule] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [addParticipantId, setAddParticipantId] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentAssignee, setCurrentAssignee] = useState<any>(null);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const [ackNotes, setAckNotes] = useState('');
  const [acknowledging, setAcknowledging] = useState(false);

  const loadSchedule = useCallback(async () => {
    if (!id || !scheduleId) return;
    try {
      const res = await store.GetRoleSchedule(id, scheduleId);
      const sch = res.schedule ?? null;
      setSchedule(sch);
      setEditForm({ name: sch?.name || '', description: sch?.description || '' });
    } catch { /* store handles */ }
  }, [id, scheduleId, store]);

  const loadCurrentAssignee = useCallback(async () => {
    if (!id || !scheduleId) return;
    setAssigneeLoading(true);
    try {
      const res = await store.GetCurrentScheduleAssignee(id, scheduleId);
      setCurrentAssignee(res);
    } catch { /* store handles */ } finally { setAssigneeLoading(false); }
  }, [id, scheduleId, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadSchedule();
    loadCurrentAssignee();
  }, [id, scheduleId, loadSchedule, loadCurrentAssignee]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !scheduleId) return;
    setSubmitting(true);
    try {
      await store.UpdateRoleSchedule(id, scheduleId, editForm as any);
      setIsEditing(false);
      showSuccess(t('team.scheduleUpdated'));
      loadSchedule();
    } catch { /* store handles */ } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!id || !scheduleId || !confirm(t('team.deleteScheduleConfirm'))) return;
    try {
      await store.DeleteRoleSchedule(id, scheduleId);
      navigate(`/teams/${id}/schedules`);
    } catch { /* store handles */ }
  };

  const handleTriggerRotation = async () => {
    if (!id || !scheduleId) return;
    try {
      await store.TriggerScheduleRotation(id, scheduleId, { schedule: refEntity(scheduleId), reason: '' });
      showSuccess(t('team.rotationTriggered'));
      loadSchedule();
      loadCurrentAssignee();
    } catch { /* store handles */ }
  };

  const handleAcknowledgeRotation = async () => {
    if (!id || !scheduleId) return;
    setAcknowledging(true);
    try {
      await store.AcknowledgeScheduleRotation(id, scheduleId, { schedule: { id: scheduleId }, notes: ackNotes.trim() } as never);
      setAckNotes('');
      showSuccess(t('team.rotationAcknowledged'));
      loadCurrentAssignee();
    } catch { /* store handles */ } finally { setAcknowledging(false); }
  };

  const handlePause = async () => {
    if (!id || !scheduleId) return;
    try {
      await store.PauseRoleSchedule(id, scheduleId, { schedule: refEntity(scheduleId), reason: '' });
      showSuccess(t('team.schedulePaused'));
      loadSchedule();
    } catch { /* store handles */ }
  };

  const handleResume = async () => {
    if (!id || !scheduleId) return;
    try {
      await store.ResumeRoleSchedule(id, scheduleId, { schedule: refEntity(scheduleId) });
      showSuccess(t('team.scheduleResumed'));
      loadSchedule();
    } catch { /* store handles */ }
  };

  const handleAddParticipant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !scheduleId || !addParticipantId.trim()) return;
    try {
      await store.AddScheduleParticipant(id, scheduleId, { schedule: refEntity(scheduleId), user: refEntity(addParticipantId.trim()) });
      setAddParticipantId('');
      showSuccess(t('team.participantAdded'));
      loadSchedule();
    } catch { /* store handles */ }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!id || !scheduleId) return;
    try {
      await store.RemoveScheduleParticipant(id, scheduleId, participantId);
      showSuccess(t('team.participantRemoved'));
      loadSchedule();
    } catch { /* store handles */ }
  };

  const teamName = getTeamName(store.currentItem) || t('team.title');

  if (store.isLoading && !schedule) {
    return (
      <div className="space-y-6">
        <div className="h-4 w-48 rounded bg-muted animate-pulse" />
        <div className="h-7 w-40 rounded bg-muted animate-pulse" />
        <div className="rounded-lg border border-border p-6 animate-pulse space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-4 w-full rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (!schedule) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to={`/teams/${id}/schedules`} className="text-sm underline text-primary">{t('team.backToSchedules')}</Link>
      </Alert>
    );
  }

  const status = getStatusMapping(scheduleStatusMap, schedule.status || '');
  const isActive = schedule.status === 'ROLE_SCHEDULE_STATUS_ACTIVE';
  const isPaused = schedule.status === 'ROLE_SCHEDULE_STATUS_PAUSED';
  const participants = schedule.participants?.actors || [];
  const rotation = schedule.rotation || {};
  const patternKey = rotationPatternMap[rotation.pattern];

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}/schedules`} className="text-muted-foreground hover:text-foreground">{t('team.nav.schedules')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{schedule.name}</span>
      </nav>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-foreground">{schedule.name}</h1>
          <Badge variant={status.variant as any} size="sm">
            {t(status.labelKey)}
          </Badge>
        </div>
        <div className="flex gap-2">
          {isActive && <Button variant="secondary" onClick={handlePause}>{t('team.pause')}</Button>}
          {isPaused && <Button variant="secondary" onClick={handleResume}>{t('team.resume')}</Button>}
          {isActive && <Button onClick={handleTriggerRotation}>{t('team.triggerRotation')}</Button>}
          <Button variant="secondary" onClick={() => setIsEditing(!isEditing)}>{isEditing ? t('common.cancel') : t('common.edit')}</Button>
          <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
        </div>
      </div>

      {/* Current assignee (live) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('team.currentAssigneeTitle')}</CardTitle>
          <Button size="sm" variant="ghost" onClick={loadCurrentAssignee} disabled={assigneeLoading}>
            {t('team.currentAssigneeRefresh')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {assigneeLoading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : currentAssignee?.assignee ? (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('common.currentAssignee')}</span>
                <p className="text-sm text-foreground mt-0.5">{currentAssignee.assignee?.user?.display_name || currentAssignee.assignee?.actor?.display_name || '--'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('team.scheduleField.period')}</span>
                <p className="text-sm text-foreground mt-0.5">{formatDateTime(currentAssignee.period_start, locale)} — {formatDateTime(currentAssignee.period_end, locale)}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('team.scheduleField.nextRotation')}</span>
                <p className="text-sm text-foreground mt-0.5">{formatDateTime(currentAssignee.next_rotation_at, locale)}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('team.noParticipants')}</p>
          )}

          {isActive && (
            <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-border">
              <Input
                placeholder={t('team.acknowledgeNotesPlaceholder')}
                value={ackNotes}
                onChange={(e) => setAckNotes(e.target.value)}
                className="flex-1"
              />
              <Button variant="secondary" onClick={handleAcknowledgeRotation} disabled={acknowledging}>
                {acknowledging ? t('common.loading') : t('team.acknowledgeRotation')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isEditing && (
        <Card>
          <form onSubmit={handleUpdate}>
            <CardContent className="space-y-4 pt-6">
              <Input label={t('team.field.name')} value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} required />
              <Input label={t('team.field.description')} value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setIsEditing(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={submitting}>{submitting ? t('common.saving') : t('common.saveChanges')}</Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {/* Schedule Info */}
      <Card>
        <CardHeader><CardTitle>{t('team.scheduleDetails')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.scheduleField.description')}</dt>
              <dd className="text-sm text-foreground col-span-2">{schedule.description || '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.scheduleField.type')}</dt>
              <dd className="text-sm text-foreground col-span-2">{schedule.schedule_type?.replace('SCHEDULE_TYPE_', '') || '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.scheduleField.currentAssignee')}</dt>
              <dd className="text-sm text-foreground col-span-2">{schedule.current_assignee?.display_name || '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.scheduleField.period')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDateTime(schedule.current_period_start, locale)} — {formatDateTime(schedule.current_period_end, locale)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('team.scheduleField.nextRotation')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDateTime(schedule.next_rotation_at, locale)}</dd>
            </div>
            {rotation.pattern && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('team.scheduleField.pattern')}</dt>
                <dd className="text-sm text-foreground col-span-2">{patternKey ? t(patternKey) : rotation.pattern}</dd>
              </div>
            )}
            {rotation.period_hours && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('team.scheduleField.periodDuration')}</dt>
                <dd className="text-sm text-foreground col-span-2">{rotation.period_hours}h ({rotation.timezone || 'UTC'})</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Participants */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team.participantsCount', { count: participants.length })}</CardTitle>
          <CardDescription>{t('team.participantsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {participants.length > 0 ? (
            <div className="space-y-2">
              {participants.map((p: any, i: number) => (
                <div key={p.entity?.id || i} className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-muted-foreground w-6">#{i + 1}</span>
                    <span className="text-sm font-medium">{p.display_name || p.name || p.entity?.id || '--'}</span>
                    {schedule.current_assignee?.entity?.id === p.entity?.id && (
                      <Badge variant="success" size="sm">{t('team.currentAssignee')}</Badge>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleRemoveParticipant(p.entity?.id || '')}>
                    {t('team.remove')}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('team.noParticipants')}</p>
          )}

          <form onSubmit={handleAddParticipant} className="flex gap-4 pt-2">
            <Input
              placeholder={t('team.participantIdPlaceholder')}
              value={addParticipantId}
              onChange={(e) => setAddParticipantId(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" disabled={!addParticipantId.trim()}>{t('team.addParticipant')}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
});

export default TeamScheduleDetailPage;
